/**
 * AI Proxy Client
 *
 * Routes all AI API calls through the Supabase Edge Function `ai-proxy`
 * so that API keys are never exposed in the browser bundle.
 *
 * Usage:
 *   const data = await aiProxy('openai', '/v1/chat/completions', body)
 *   const data = await aiProxy('anthropic', '/v1/messages', body, extraHeaders)
 *
 * Deliberately NOT supabase.functions.invoke(): that resolves the JWT via
 * auth.getSession(), which hangs forever when the client's token refresh is
 * wedged after a long idle (the "refreshSession timed out" warnings). A scan
 * or extraction would then spin indefinitely with no error. We fetch the
 * function directly with a bounded token lookup, a hard request timeout, and
 * the upstream error body surfaced so the user sees what actually failed.
 */

import { getAccessTokenSafe } from '@/lib/supabase/client'

type Provider = 'openai' | 'anthropic' | 'perplexity' | 'google-gemini'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/ai-proxy`

/** Large PDFs through Claude can take a while; the edge runtime caps well under this. */
const REQUEST_TIMEOUT_MS = 120_000

/** Pull a human-readable message out of an OpenAI / Anthropic / proxy error body. */
function upstreamErrorMessage(status: number, text: string): string {
  try {
    const json = JSON.parse(text) as { error?: unknown; message?: unknown }
    const err = json.error
    if (typeof err === 'string') return err
    if (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string') {
      return (err as { message: string }).message
    }
    if (typeof json.message === 'string') return json.message
  } catch {
    /* not JSON */
  }
  if (status === 401) return 'Your session has expired — reload the page and try again'
  return text.slice(0, 300) || `HTTP ${status}`
}

/**
 * Make an authenticated POST request through the ai-proxy Edge Function.
 */
export async function aiProxy<T = unknown>(
  provider: Provider,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const token = await getAccessTokenSafe()

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  let response: Response
  try {
    response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'x-provider': provider,
        'x-target-url': path,
        ...extraHeaders,
      },
      body: JSON.stringify(body ?? {}),
      signal: controller.signal,
    })
  } catch (err) {
    if (controller.signal.aborted) {
      throw new Error(`${provider} API error: request timed out after ${REQUEST_TIMEOUT_MS / 1000}s`)
    }
    throw new Error(
      `${provider} API error: ${err instanceof Error ? err.message : 'network request failed'}`,
    )
  } finally {
    clearTimeout(timer)
  }

  const text = await response.text()
  if (!response.ok) {
    throw new Error(`${provider} API error: ${upstreamErrorMessage(response.status, text)}`)
  }

  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error(`${provider} API error: unreadable response`)
  }
}
